import { motion } from 'framer-motion'

type Props = {
  yesTitle: string
  yes: readonly string[]
  noTitle: string
  no: readonly string[]
  note?: string
}

const CARD_REVEAL = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
}

/**
 * Tableau OUI / NON des pages Expertise — justesse du périmètre légal.
 * Colonne OUI (vert, ✓) et colonne NON (atténuée, ✗). Centré sur mobile.
 */
export default function ScopeTable({ yesTitle, yes, noTitle, no, note }: Props) {
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-4 sm:gap-5">
        {/* ── OUI ── */}
        <motion.div
          {...CARD_REVEAL}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-2xl sm:rounded-3xl border border-emerald-200 bg-emerald-50/40 p-6 sm:p-8 flex flex-col items-center md:items-start text-center md:text-left overflow-hidden"
        >
          <span
            aria-hidden
            className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-2xl pointer-events-none opacity-60"
            style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.22), transparent 70%)' }}
          />
          <div className="relative flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-[0_10px_24px_-10px_rgba(16,185,129,0.8)]">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6">
                <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <h3 className="text-base sm:text-lg font-semibold text-ink-950 tracking-tight">
              {yesTitle}
            </h3>
          </div>
          <ul className="relative mt-5 w-full space-y-2.5">
            {yes.map((item, i) => (
              <motion.li
                key={item}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: 0.05 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-start gap-2.5 justify-center md:justify-start"
              >
                <svg
                  className="mt-0.5 shrink-0 text-emerald-600"
                  width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"
                  aria-hidden
                >
                  <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-sm sm:text-base text-ink-700 leading-snug">{item}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* ── NON ── */}
        <motion.div
          {...CARD_REVEAL}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-2xl sm:rounded-3xl border border-ink-200 bg-ink-100 p-6 sm:p-8 flex flex-col items-center md:items-start text-center md:text-left"
        >
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-xl bg-ink-200 text-ink-500 flex items-center justify-center">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6">
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <h3 className="text-base sm:text-lg font-semibold text-ink-600 tracking-tight">
              {noTitle}
            </h3>
          </div>
          <ul className="mt-5 w-full space-y-2.5">
            {no.map((item, i) => (
              <motion.li
                key={item}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-start gap-2.5 justify-center md:justify-start"
              >
                <svg
                  className="mt-0.5 shrink-0 text-ink-400"
                  width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                  aria-hidden
                >
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-sm sm:text-base text-ink-500 leading-snug line-through decoration-ink-300/70">
                  {item}
                </span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>

      {note && (
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-5 sm:mt-6 text-sm text-ink-500 leading-relaxed max-w-2xl mx-auto md:mx-0 text-center md:text-left"
        >
          {note}
        </motion.p>
      )}
    </div>
  )
}
